// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import {
  ClearChatAction, ResetSessionAction, RestoreSessionAction, SendChatTurnDoneAction,
  SendChatTurnFailureAction, SetSessionStaleAction,
} from './chatbotActions';
import {
  getChatError, getChatLoading, getChatMessages, getChatProgress, getSessionStale,
} from './chatbotSelectors';
import {
  LoadSessionThunk, PollProgressThunk, SaveSessionThunk, SendChatTurnThunk, stripImageBlocks,
} from './chatbotThunk';
import type {
  ChatAppAction, ChatAppState, ChatChartSpec, ChatEndpoint, ChatFeatureAnalysis, ChatHighlight, ChatImageBlock,
  ChatMessage, ChatPanoCommand, ChatQueryResult, ChatS3Ref, ChatSpatialLayer, ChatWidget,
} from './chatbotTypes';
import type { PanoViewContext } from './panoImage';

// Re-export the chat types so existing importers (dashboardSlice, chatExport, ChatChart, index) keep
// importing them from './useChatbot' unchanged.
export * from './chatbotTypes';

// Hard ceiling for one chat turn. Generous — a heavy multi-tool agent turn can run a few minutes — but
// bounded so a request that never returns releases the input instead of hanging the UI indefinitely.
const REQUEST_TIMEOUT_MS = 300_000;

// Drop the per-feature measure arrays (`data[i]._key_values`) before persisting a chart in the session.
// These exist ONLY so a dashboard-PINNED chart can re-aggregate SUM/AVG/MIN/MAX against the map extent
// (chartViewRecompute.ts); the in-transcript chart never reads them, and a chart re-pinned from a restored
// transcript degrades gracefully to full-extent (its `recompute` gate falls back when they're absent).
// `_keys` is KEPT (the transcript's click-to-highlight reads rows[i]._keys) and so is `frames` (the
// parametric slider) — stripping either would reduce restored-session functionality, which we don't do.
const stripChartRecomputeState = (chart: ChatChartSpec): ChatChartSpec => ({
  ...chart,
  data: chart.data.map(({ _key_values, ...row }) => row),
});

/**
 * Headless orchestrator for the Khora chat: the conversation state lives in the chatbot slice (read via
 * selectors), the endpoint calls live in chatbotThunk, and this hook wires the per-turn orchestration
 * (abort/timeout, live-progress polling) plus the caller-injected artifact bridges (highlights, spatial
 * layers, feature analyses, session artifacts) so the component tree stays the Redux boundary.
 */
export const useChatbot = (
  buildAppState?: () => ChatAppState,
  getEndpoint?: () => ChatEndpoint | undefined,
  onWidget?: (widget: ChatWidget) => void,
  onHighlights?: (highlights: ChatHighlight[]) => void,
  onSpatialLayers?: (layers: ChatSpatialLayer[], refs: ChatS3Ref[]) => void,
  // Stable per-gdb/order session identity (the order id). When it changes, the prior session is
  // hydrated from the server; the transcript itself is NEVER stored client-side (PII-adjacent).
  sessionKey?: string,
  // Supplies / consumes the non-message session artifacts (viewer layers, dashboard pins) so they
  // persist + restore alongside the conversation. Callbacks keep the message plumbing decoupled from
  // the artifact slices, which the caller (which holds Redux) reads/dispatches.
  buildSessionArtifacts?: () => { session: Record<string, unknown>; s3_refs: ChatS3Ref[] },
  onRestoreArtifacts?: (session: Record<string, unknown>, s3Refs: ChatS3Ref[]) => void,
  // Per-feature analysis (add_feature_analysis) to inject into existing feature popups (not a layer).
  // Receives the turn's s3_refs too, so an OFFLOADED analysis (by_key moved to S3) can be rehydrated.
  onFeatureAnalyses?: (analyses: ChatFeatureAnalysis[], refs: ChatS3Ref[]) => void,
  // Clear the caller's per-order viewer artifacts (Dashboard layers + pins + highlight + analysis) when
  // the session (order/gdb) switches — fired BEFORE the new order's session restores, so nothing bleeds.
  onSessionReset?: () => void,
  // Open the nearest pano to a pole (go_to_pano) and orient the 360 view toward it.
  onPano?: (cmd: ChatPanoCommand) => void,
  // Khora-initiated map actions (trigger_app_action) — draw_region / clear_region.
  onActions?: (actions: ChatAppAction[]) => void,
  // The last tabular run_sql result → the viewer's bottom rows-panel (null when the turn produced none).
  onQueryResult?: (result: ChatQueryResult | null) => void,
) => {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(getChatMessages);
  const loading = useAppSelector(getChatLoading);
  const progress = useAppSelector(getChatProgress);
  const error = useAppSelector(getChatError);
  const sessionStale = useAppSelector(getSessionStale);

  // Keep the latest callbacks + guard values readable inside send()/effects without re-creating them.
  const buildAppStateRef = useRef(buildAppState);
  buildAppStateRef.current = buildAppState;
  const getEndpointRef = useRef(getEndpoint);
  getEndpointRef.current = getEndpoint;
  const onWidgetRef = useRef(onWidget);
  onWidgetRef.current = onWidget;
  const onHighlightsRef = useRef(onHighlights);
  onHighlightsRef.current = onHighlights;
  const onSpatialLayersRef = useRef(onSpatialLayers);
  onSpatialLayersRef.current = onSpatialLayers;
  const buildSessionArtifactsRef = useRef(buildSessionArtifacts);
  buildSessionArtifactsRef.current = buildSessionArtifacts;
  const onRestoreArtifactsRef = useRef(onRestoreArtifacts);
  onRestoreArtifactsRef.current = onRestoreArtifacts;
  const onFeatureAnalysesRef = useRef(onFeatureAnalyses);
  onFeatureAnalysesRef.current = onFeatureAnalyses;
  const onSessionResetRef = useRef(onSessionReset);
  onSessionResetRef.current = onSessionReset;
  const onPanoRef = useRef(onPano);
  onPanoRef.current = onPano;
  const onActionsRef = useRef(onActions);
  onActionsRef.current = onActions;
  const onQueryResultRef = useRef(onQueryResult);
  onQueryResultRef.current = onQueryResult;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  // The order/gdb currently loaded. A turn captures this at send time and the thunk re-checks it before
  // committing, so a reply that lands AFTER the user switched orders is dropped instead of bleeding into
  // the newly-selected order.
  const sessionKeyRef = useRef(sessionKey);
  sessionKeyRef.current = sessionKey;

  // One in-flight-request controller so a hung chat POST can be aborted — on a hard timeout (below), on
  // unmount, and on an order/gdb switch — instead of leaving `loading` true and the input locked forever
  // when the request never returns (dropped connection, server hang), or letting it resolve into the wrong order.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  // Persist the session server-side after each turn — NEVER localStorage (which already holds the
  // plaintext token/user; the message array is PII-adjacent). Fire-and-forget: a failure never blocks
  // the chat, and the raw run_sql rows never leave the Lambda (the client array is text/artifact only).
  const persistSession = useCallback((msgs: ChatMessage[]) => {
    const endpoint = getEndpointRef.current?.();
    // Sessions are stored PER GEODATABASE VERSION — no loaded gdb yet means nothing to key by.
    if (!endpoint?.url || !endpoint.gdbId) return;
    // Include the non-message artifacts (viewer layers + dashboard pins) + their S3 keys so the server
    // can re-presign them on load. Still text/aggregated only — no raw run_sql rows.
    const artifacts = buildSessionArtifactsRef.current?.() ?? { session: {}, s3_refs: [] };
    // Fold every large-export file key (message.files[].key) into the session s3_refs alongside the layer
    // keys, so load() re-presigns a FRESH download URL for each — otherwise a reopened session's download
    // button points at the long-expired URL saved with the message. Deduped by key against the layer refs.
    const fileRefs: ChatS3Ref[] = msgs.flatMap((message) => (message.files ?? [])
      .filter((file) => file.key)
      .map((file) => ({ key: file.key as string })));
    const s3_refs = Array.from(new Map([...artifacts.s3_refs, ...fileRefs].map((ref) => [ref.key, ref])).values());
    // Never persist the (large) pano-vision image blocks — they're transient analysis inputs, not
    // conversation content, and storing them bloats the session + re-hydrates the OOM-inducing accumulation.
    // Drop the (potentially large) highlight geojson before persisting — it's only needed live for the
    // "Pin to map" button; a layer the user actually pinned already persists via viewerLayers.
    const leanMsgs = msgs.map((message) => ({
      ...message,
      content: stripImageBlocks(message.content),
      highlights: undefined,
      charts: message.charts?.map(stripChartRecomputeState),
    }));
    dispatch(SaveSessionThunk(endpoint, leanMsgs, { ...artifacts, s3_refs }));
  }, [dispatch]);

  // Resume the prior session when the gdb/order (sessionKey) changes. Server-authoritative + defensive:
  // clear first so a session never bleeds across gdbs, then hydrate; a load failure or a malformed
  // payload leaves an empty conversation rather than crashing.
  useEffect(() => {
    if (!sessionKey) return undefined;
    let cancelled = false;
    dispatch(ResetSessionAction());
    // Clear the prior order's viewer artifacts NOW (synchronously), before the async restore below
    // re-adds THIS order's — so Dashboard layers/pins/highlight/analysis never bleed across orders/gdbs.
    onSessionResetRef.current?.();
    (async () => {
      const endpoint = getEndpointRef.current?.();
      if (!endpoint?.url || !endpoint.gdbId) return;
      const loaded = await dispatch(LoadSessionThunk(endpoint));
      // Drop a stale response if the gdb/order switched mid-load (a newer effect run owns the state now).
      if (cancelled || !loaded) return;
      // Re-point each restored message's large-export downloads at the FRESH presigned URL load() just
      // minted (joined by S3 key) — the URL saved with the message expired long ago. Small inline-b64
      // files (no key) are untouched.
      const urlByKey = new Map((loaded.s3_refs ?? []).map((ref) => [ref.key, ref.url]));
      const restoredMessages = loaded.messages.map((message) => (message.files?.some((file) => file.key)
        ? { ...message, files: (message.files ?? []).map((file) => (file.key ? { ...file, url: urlByKey.get(file.key) ?? file.url } : file)) }
        : message));
      // Sessions are PER VERSION: always restore this gdb's own conversation; `stale` is just the
      // dismissable "a newer version of this order exists" hint.
      dispatch(RestoreSessionAction({ messages: restoredMessages, stale: loaded.stale }));
      // Re-dispatch the restored viewer layers + dashboard pins (freshened s3_refs joined by key).
      onRestoreArtifactsRef.current?.(loaded.session, loaded.s3_refs);
    })();
    // Abort a turn still in flight for the PREVIOUS order so it can't resolve into this one (the thunk's
    // isCurrent check is the backstop if the reply already arrived).
    return () => { cancelled = true; abortRef.current?.abort(); };
  }, [sessionKey, dispatch]);

  const send = useCallback(async (text: string, image?: ChatImageBlock, panoView?: PanoViewContext) => {
    const trimmed = text.trim();
    if (!trimmed || loadingRef.current) {
      return;
    }
    // A per-turn id so the server can key the live tool-progress object the Lambda writes and this
    // client polls. UUID-ish so it satisfies the server's request_id validation.
    const requestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `req-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
    const endpoint = getEndpointRef.current?.();
    // The order/gdb this turn belongs to; the thunk drops the reply if this is no longer the loaded order.
    const startSessionKey = sessionKeyRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    let timedOut = false;
    const timeoutId = setTimeout(() => { timedOut = true; controller.abort(); }, REQUEST_TIMEOUT_MS);
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    try {
      // Poll the server for the agent's LIVE tool status while the turn runs (only with a server
      // endpoint that stores progress). Best-effort — a failed/empty poll never affects the turn.
      if (endpoint?.url) {
        pollTimer = setInterval(() => { dispatch(PollProgressThunk(endpoint, requestId)); }, 1200);
      }
      const result = await dispatch(SendChatTurnThunk(trimmed, {
        endpoint,
        appState: buildAppStateRef.current?.(),
        requestId,
        signal: controller.signal,
        image, // opt-in pano-vision frame (undefined unless the pano-viewer toggle is on)
        panoView, // opt-in pano-view geometry so Khora can resolve which pole the frame shows
        isCurrent: () => sessionKeyRef.current === startSessionKey, // still the same order/gdb?
      }));
      if (result.status === 'ok') {
        persistSession(result.messages); // save the session server-side after each completed turn
        const { data } = result;
        // Widgets are NOT auto-pinned — they ride the message and the bubble offers a "Pin as widget to
        // dashboard" button so the USER decides. onWidget is kept for that button to call.
        if (data.highlights?.length) onHighlightsRef.current?.(data.highlights);
        if (data.spatial_layers?.length) onSpatialLayersRef.current?.(data.spatial_layers, data.s3_refs ?? []);
        if (data.feature_analyses?.length) onFeatureAnalysesRef.current?.(data.feature_analyses, data.s3_refs ?? []);
        if (data.pano) onPanoRef.current?.(data.pano);
        if (data.actions?.length) onActionsRef.current?.(data.actions);
        // Always forward (even null) so a turn without a tabular result CLEARS a stale query table.
        onQueryResultRef.current?.(data.query_result ?? null);
      } else if (result.status === 'aborted' && timedOut) {
        dispatch(SendChatTurnFailureAction('The request took too long and was cancelled. Please try again.'));
      }
      // 'error' → the thunk already dispatched the failure; 'aborted' on unmount → stay silent.
    } finally {
      clearTimeout(timeoutId);
      if (abortRef.current === controller) abortRef.current = null;
      if (pollTimer) clearInterval(pollTimer);
      dispatch(SendChatTurnDoneAction()); // release the input (loading off) + clear progress
    }
  }, [dispatch, persistSession]);

  // Clear the conversation: wipe the transcript + reset the error, and overwrite the server-saved
  // session with an empty message list so a reload doesn't restore it. The viewer layers and dashboard
  // pins Khora produced are left in place (they have their own remove controls) — persistSession
  // re-includes them, so the map/board state survives a chat clear.
  const clear = useCallback(() => {
    dispatch(ClearChatAction());
    persistSession([]);
  }, [dispatch, persistSession]);

  // persistSession is exposed so the caller can re-save after a dashboard change (pin/unpin) — not just
  // after a chat turn — so a removed pin actually stays removed across a reload.
  return {
    messages,
    loading,
    error,
    send,
    clear,
    progress,
    sessionStale,
    dismissStale: () => dispatch(SetSessionStaleAction(false)),
    persistSession,
  };
};
